import './meetingSider.scss';
import { useEffect } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { useMeetingStore } from '@/stores/meeting.ts';
import ParticipantsView from '@/components/MeetingSider/components/ParticipantsView';
import ChatView from '@/components/MeetingSider/components/ChatView';
import DocsView from '@/components/MeetingSider/components/DocsView';
import { useMessagesStore } from '@/stores/messages';
import { ChatMessage } from '@/types/messages';

interface MeetingSiderProps {
  assistantTokenForInvite: string | null;
  loading: boolean;
}

function MeetingSider({ assistantTokenForInvite, loading }: MeetingSiderProps) {
  const { activeToolPanel } = useMeetingStore();
  const { addUniqueMessage } = useMessagesStore();
  const { incrementUnread } = useMessagesStore();
  const room = useRoomContext();
  const meetingTitle = useMeetingStore((state) => state.meeting?.title) || '';

  useEffect(() => {
    if (!room || !meetingTitle) return;

    room.registerTextStreamHandler(meetingTitle, async (reader, _) => {
      const info = reader.info;
      const attrs = info.attributes ?? {};
      const text = await reader.readAll();

      const msg: ChatMessage = {
        id: attrs.messageId,
        author: attrs.author,
        authorId: attrs.authorId,
        text,
        fromMe: attrs.authorId === room.localParticipant.identity,
        createdAt: new Date(info.timestamp),
        meetingTitle: attrs.meetingTitle ?? '',
        status: 'sent'
      };

      addUniqueMessage(msg);
      incrementUnread();
    });

    return () => {
      room.unregisterTextStreamHandler(meetingTitle);
    };
  }, [room, meetingTitle]);

  return (
    <aside className="sider">
      {activeToolPanel === 'participants' && (
        <ParticipantsView loading={loading} assistantTokenForInvite={assistantTokenForInvite} />
      )}
      {activeToolPanel === 'chat' && <ChatView />}
      {activeToolPanel === 'docs' && <DocsView />}
    </aside>
  );
}

export default MeetingSider;
